/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ExtractedErrorText } from '@app/core/types/index';
import { ConvertedQueryResponseResult } from '@app/sql/types';
import { NodeId } from '@shared/types/node';

export enum SystemView {
  // Common System views
  'BASELINE_NODES' = 'BASELINE_NODES',
  'BINARY_METADATA' = 'BINARY_METADATA',
  'CACHES' = 'CACHES',
  'CACHE_GROUPS' = 'CACHE_GROUPS',
  'CACHE_GROUP_PAGE_LISTS' = 'CACHE_GROUP_PAGE_LISTS',
  'CLIENT_CONNECTIONS' = 'CLIENT_CONNECTIONS',
  'CLIENT_CONNECTION_ATTRIBUTES' = 'CLIENT_CONNECTION_ATTRIBUTES',
  'CONTINUOUS_QUERIES' = 'CONTINUOUS_QUERIES',
  'DATASTREAM_THREADPOOL_QUEUE' = 'DATASTREAM_THREADPOOL_QUEUE',
  'DATA_REGION_PAGE_LISTS' = 'DATA_REGION_PAGE_LISTS',
  'DS_ATOMICLONGS' = 'DS_ATOMICLONGS',
  'DS_ATOMICREFERENCES' = 'DS_ATOMICREFERENCES',
  'DS_ATOMICSEQUENCES' = 'DS_ATOMICSEQUENCES',
  'DS_ATOMICSTAMPED' = 'DS_ATOMICSTAMPED',
  'DS_COUNTDOWNLATCHES' = 'DS_COUNTDOWNLATCHES',
  'DS_QUEUES' = 'DS_QUEUES',
  'DS_REENTRANTLOCKS' = 'DS_REENTRANTLOCKS',
  'DS_SEMAPHORES' = 'DS_SEMAPHORES',
  'DS_SETS' = 'DS_SETS',
  'INDEXES' = 'INDEXES',
  'JOBS' = 'JOBS',
  'LOCAL_CACHE_GROUPS_IO' = 'LOCAL_CACHE_GROUPS_IO',
  'METRICS' = 'METRICS',
  'NODES' = 'NODES',
  'NODE_ATTRIBUTES' = 'NODE_ATTRIBUTES',
  'NODE_METRICS' = 'NODE_METRICS',
  'SCAN_QUERIES' = 'SCAN_QUERIES',
  'SCHEMAS' = 'SCHEMAS',
  'SERVICES' = 'SERVICES',
  'SQL_QUERIES' = 'SQL_QUERIES',
  'SQL_QUERIES_HISTORY' = 'SQL_QUERIES_HISTORY',
  'STATISTICS_CONFIGURATION' = 'STATISTICS_CONFIGURATION',
  'STATISTICS_LOCAL_DATA' = 'STATISTICS_LOCAL_DATA',
  'STATISTICS_PARTITION_DATA' = 'STATISTICS_PARTITION_DATA',
  'STRIPED_THREADPOOL_QUEUE' = 'STRIPED_THREADPOOL_QUEUE',
  'TABLES' = 'TABLES',
  'TABLE_COLUMNS' = 'TABLE_COLUMNS',
  'TASKS' = 'TASKS',
  'TRANSACTIONS' = 'TRANSACTIONS',
  'VIEWS' = 'VIEWS',
  'VIEW_COLUMNS' = 'VIEW_COLUMNS',
  //Ignite additional views
  'BASELINE_NODE_ATTRIBUTES' = 'BASELINE_NODE_ATTRIBUTES',
  'CONFIGURATION' = 'CONFIGURATION',
  'DISTRIBUTED_METASTORAGE' = 'DISTRIBUTED_METASTORAGE',
  'METASTORAGE' = 'METASTORAGE',
  'PARTITION_STATES' = 'PARTITION_STATES',
  'SNAPSHOT' = 'SNAPSHOT',
}
export const SystemViewTuple = [...Object.values(SystemView)] as const;
export type SystemViewType = (typeof SystemViewTuple)[number];

export type SqlQueryString = string;

export const LimitRows = [1, 5, 15, 25, 50, 100, 200, 500, 1000] as const;
export type LimitRowsType = (typeof LimitRows)[number];

// update interval for query updates in minutes
export const UpdateIntervals = [0.0833333333, 0.5, 1, 5, 15, 30, 60, 180] as const;
export type UpdateIntervalType = (typeof UpdateIntervals)[number];

export enum QueryWidgetType {
  'SYSTEM_VIEW' = 'SYSTEM_VIEW',
  'SQL' = 'SQL',
}

export type QueryParams = {
  limitRows: LimitRowsType;
  nodeConsistentId: NodeId | null;
  updateInterval: UpdateIntervalType;
};

export type QueryWidgetSettings<T extends QueryWidgetType.SYSTEM_VIEW | QueryWidgetType.SQL> = {
  queryWidgetType: T;
  params: QueryParams & {
    query: T extends QueryWidgetType.SYSTEM_VIEW ? SystemViewType | null : SqlQueryString;
  };
};

export enum QueryErrorType {
  'REQUEST_FAILED' = 'REQUEST_FAILED',
  'NOT_AUTHORIZED' = 'NOT_AUTHORIZED',
}

export type QueryResponse = ConvertedQueryResponseResult &
  (
    | {
        errorType: undefined;
        error: undefined;
      }
    | {
        errorType: QueryErrorType;
        error: ExtractedErrorText;
      }
  );
